import { PersonType } from 'es/app/business-logic/domain-models/People';
import { PEOPLE } from './graphql/root.people';
import { UniversalPersonSearchQuery, UniversalPersonSearchQueryVariables } from 'es/app/__generated__/global';
import ApolloProvider from 'es/core/net/ApolloProvider';
import Guid, { guidEquals } from 'es/core/types/Guid';
import { IUniversalSearchCriteria } from './UniversalSearch';

export interface IPersonMatch {
    id: Guid;
    firstName: string | null;
    lastName: string | null;
    emailAddress: string | null;
    type: PersonType;
    username?: string | null;
    apiCredentialId: Guid | null;
    apiKey: Guid | null;
}

export async function universalPeopleSearch(filter: IUniversalSearchCriteria) {
    const matches = await findByTextOrId(filter);
    if (matches.length) return matches;

    // Search for a matching API credential if we didn't find any matches... this is a slow search so we don't
    // want to do it unless we have to.
    return await findByApiCredential(filter.id);
}

async function findByTextOrId(filter: IUniversalSearchCriteria) {
    if (!filter.text) {
        return [];
    }

    const { data } = await ApolloProvider.root.query<UniversalPersonSearchQuery, UniversalPersonSearchQueryVariables>({
        query: PEOPLE,
        variables: {
            filter: {
                any_contains: filter.text ?? undefined,
                id: filter.id ?? undefined,
            },
        },
    });

    return mapResults(data);
}

async function findByApiCredential(id: Guid | null) {
    if (!id) return [];

    const { data } = await ApolloProvider.root.query<UniversalPersonSearchQuery, UniversalPersonSearchQueryVariables>({
        query: PEOPLE,
    });

    const people = mapResults(data);
    return people.filter(
        person =>
            (person.apiKey && guidEquals(person.apiKey, id)) ||
            (person.apiCredentialId && guidEquals(person.apiCredentialId, id))
    );
}

function mapResults(data: UniversalPersonSearchQuery): IPersonMatch[] {
    return data?.system.contacts.map(person => {
        const username = person?.user?.username;
        const apiCredential = person?.user?.credentials[0];
        const result: IPersonMatch = {
            ...person,
            type: person?.user ? PersonType.User : PersonType.Contact,
            username,
            apiCredentialId: apiCredential?.id ?? null,
            apiKey: apiCredential?.apiKey ?? null,
        };
        return result;
    });
}
